import React, { useContext, useEffect, useMemo, useState } from 'react'
import objectPath from 'object-path'
import { useRouteMatch, useLocation } from 'react-router-dom'
import {
  Modal,
  Row,
  Col,
  Form,
  Button,
  ButtonToolbar,
  ToggleButtonGroup,
  ToggleButton,
} from 'react-bootstrap'
import { DesignContext } from 'contexts/DesignContext'
import { MuiThemeProvider } from '@material-ui/core'
import { themeDefault } from 'designer/component/display/themes/layoutTheme'
import { JsonForms } from '@jsonforms/react'
import { materialCells } from '@jsonforms/material-renderers'
import { createFavoriteRenderer } from 'designer/component/control/renderer'
import FeatherIcon from 'feather-icons-react'
import useAuth from 'hooks/useAuth'
import httpClient from 'utility/httpClient.util'
import Swal from 'sweetalert2'
import { useFormik } from 'formik'
import {
  createRenderTableData,
  createSelectFavorite,
} from './SelectFavorite'

const ModalCreate = (props) => {
  const {
    isOpen = false,
    setIsOpen,
    uischema,
    isUpdated = false,
    onFetch = () => {},
    isTable,
    config,
    dynamicData = {},
  } = props
  const mode = isUpdated ? 'แก้ไขสูตร' : 'สร้างสูตร'
  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    getCurrent,
    setFormData,
    isUiChanged,
    setIsUiChanged,
    searchData,
    setComponentPropsData,
    tabParamActive,
    getCurrentUi,
    currentSearch,
    setTableRefresh,
  } = designContext
  const match = useRouteMatch()
  const location = useLocation()
  let receivePathParams = match?.params || {}
  let urlSearchParams = new URLSearchParams(location.search || [])
  let receiveQueryParams = Object.fromEntries([...urlSearchParams])
  let allParams = { ...receivePathParams, ...receiveQueryParams }
  const search = useMemo(() => {
    let searchVal = jsonForm.search || {}
    if (currentSearch?.uiKey && currentSearch?.search) {
      searchVal = currentSearch?.search
    }
    return searchVal
  }, [currentSearch?.uiKey])
  const formData = objectPath.get(jsonForm, 'formData')
  let { searchDataFromPage = {} } = config || {}
  const jsonTable =
    objectPath.get(jsonForm.formData, uischema.key) ||
    objectPath.get(jsonForm.formData, uischema.name) ||
    []
  const [tableData, setTableData] = useState(jsonTable)
  const [searchPath, setSearchPath] = useState('')
  const [searchString, setSearchString] = useState('')
  const [loading, setLoading] = useState(false)
  const { user } = useAuth()
  const favorites = jsonForm.favorites || []
  const favoriteUi = uischema.favorite
  const favoriteTemplateTypeCode = objectPath.get(
    uischema,
    'favorite.favoriteTemplateTypeCode',
  )

  const favoriteKey = objectPath.get(uischema, 'favorite.key')
  const favoriteItem = useMemo(() => {
    if (!isOpen) return {}
    let favoriteItem
    let otherFavoriteItem
    let otherFavorites = objectPath.get(isUiChanged, 'tabForm')
    if (otherFavorites) {
      Object.values(otherFavorites).forEach(({ jsonForm }) => {
        let favorites = jsonForm.favorites
        let found = favorites.find((item) => item.key === favoriteKey)
        if (found) {
          otherFavoriteItem = found
        }
      })
    }
    if (favorites) {
      favoriteItem = favorites.find((item) => item.key === favoriteKey)
    }
    return otherFavoriteItem || favoriteItem || {}
  }, [isOpen])

  const mapFieldFavorite =
    objectPath.get(favoriteItem, 'arraySelect.mapField') || []
  const favoriteDataKey = objectPath.get(
    favoriteItem,
    `jsonData.${uischema.key}`,
  )
  const favoriteJsonName = objectPath.get(favoriteItem, `jsonName`)
  const favoriteSelectType = objectPath.get(favoriteItem, 'selectType')
  const { fieldKey, favoriteType } = objectPath.get(favoriteItem) || {}

  const data = objectPath.get(isUiChanged, `editFavorite`) || {}
  const jsonData = favoriteDataKey
    ? objectPath.get(data, `jsonData.${favoriteDataKey}`) || null
    : objectPath.get(data, 'jsonData') || null

  const initialValues = {
    name: '',
    userId: objectPath.get(user, 'userId'),
    jsonData: '',
    description: '',
    favoriteTemplateTypeCode: objectPath.get(
      uischema,
      'favorite.favoriteTemplateTypeCode',
    ),
    isPublic: 1,
  }

  const formik = useFormik({
    initialValues: initialValues,
    validate: (values) => {
      let errors = {}
      if (!values.name) {
        errors.name = 'Required!'
      }
      if (!values.favoriteTemplateTypeCode) {
        errors.favoriteTemplateTypeCode = 'Required!'
      }
      return errors
    },
    onSubmit: (values, isSelected) => saveFavorite(values, isSelected),
  })

  const favorite = formik?.values || {}
  const favoriteObj = objectPath(favorite)
  const formDataObj = objectPath(formData)
  const favoriteTemplateId = favoriteObj.get('favoriteTemplateId')

  const closeModal = () => {
    setIsOpen(false)
    onFetch()
    formik.setValues(initialValues)
    objectPath.set(isUiChanged, 'editFavorite', {})
    setIsUiChanged(isUiChanged)
  }

  const onAction = {
    setLoading,
    setPage: () => {},
    setRowsPerPage: () => {},
    setTotalCount: () => {},
    setTableColumn: () => {},
    setTableData,
    setSearchPath,
    setSearchString,
    setComponentPropsData,
    setFormData,
    updateData: () => {},
  }

  const renderTableData = createRenderTableData({
    jsonForm,
    isUiChanged,
    formData,
    searchData,
    searchDataFromPage,
    allParams,
    tabParamActive,
    search,
    dynamicData,
    onAction,
    searchPath,
    searchString,
    tableData,
    loading,
    designContext,
    setFormData,
    setTableRefresh,
    setLoading,
  })

  const selectFavorite = createSelectFavorite({
    uischema,
    favorite,
    favoriteJsonName,
    favoriteSelectType,
    mapFieldFavorite,
    formData,
    designContext,
    allParams,
    user,
    jsonForm,
    isUiChanged,
    setFormData,
    setIsUiChanged,
    renderTableData,
    onCloseModal: closeModal,
    getCurrentUi,
  })

  const updateValue = (key, value) => {
    if (key !== 'jsonData' && key.startsWith('jsonData.')) {
      if (
        typeof favorite.jsonData !== 'object' ||
        favorite.jsonData === null ||
        Array.isArray(favorite.jsonData)
      ) {
        objectPath.set(favorite, 'jsonData', {})
      }
    }
    objectPath.set(favorite, key, value)
    formik.setValues(favorite)
    if (key === 'jsonData') {
      if (favoriteDataKey) {
        objectPath.set(data, `jsonData.${favoriteDataKey}`, value)
      } else {
        objectPath.set(data, 'jsonData', value)
      }
    } else {
      objectPath.set(data, key, value)
    }
    objectPath.set(isUiChanged, 'editFavorite', data)
  }

  const saveFavorite = (values) => {
    let isSaveAndUsed = values.isSaveAndUsed
    delete values.isSaveAndUsed

    if (favoriteType === 'Table') {
      let data = jsonData
      let newData = data.map((rowValue) => {
        let newValue = {}
        mapFieldFavorite.forEach(({ field }) => {
          let value = objectPath.get(rowValue, field)
          objectPath.set(newValue, field, value)
        })
        return newValue
      })
      objectPath.set(values, `jsonData`, {})
      if (favoriteJsonName) {
        objectPath.set(values, `jsonData.${favoriteJsonName}`, newData)
      } else {
        objectPath.set(values, `jsonData.${favoriteDataKey}`, newData)
      }
    } else {
      objectPath.set(values, `jsonData`, {})
      if (favoriteDataKey) {
        objectPath.set(values, `jsonData.${favoriteDataKey}`, jsonData)
      } else {
        objectPath.set(values, `jsonData`, jsonData)
      }
    }

    if (isUpdated) {
      if (!favoriteTemplateId) return
      httpClient
        .put(`core/favorite-template/${favoriteTemplateId}`, values)
        .then(({ data }) => {
          Swal.fire({
            title: 'แก้ไข Favorite สำเร็จ',
            description: JSON.stringify(data.data),
            icon: 'success',
            confirmButtonColor: '#7367f0',
            confirmButtonText: 'ปิด',
          }).then(async (result) => {
            if (result.isConfirmed) {
              if (isSaveAndUsed) {
                selectFavorite()
              } else {
                closeModal()
              }
            }
          })
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'ไม่สามารถแก้ไข Favorite ได้',
            text: 'กรุณาลองใหม่อีกครั้ง',
            confirmButtonText: 'ปิด',
          })
        })
    } else {
      httpClient
        .post('core/favorite-template', values)
        .then(({ data }) => {
          Swal.fire({
            title: 'สร้าง Favorite สำเร็จ',
            description: JSON.stringify(data.data),
            icon: 'success',
            confirmButtonColor: '#7367f0',
            confirmButtonText: 'ปิด',
          }).then(async (result) => {
            if (result.isConfirmed) {
              if (isSaveAndUsed) {
                selectFavorite()
              } else {
                closeModal()
              }
            }
          })
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'ไม่สามารถสร้าง Favorite ได้',
            text: 'กรุณาลองใหม่อีกครั้ง',
            confirmButtonText: 'ปิด',
          })
        })
    }
  }

  const [uiList, setUiList] = useState([])
  const [schemaList, setSchemaList] = useState([])

  useEffect(() => {
    if (!isOpen) return
    let uiList = []
    let schemaList = []
    let jsonDataSchema = objectPath.get(uischema, 'favorite.jsonData') || {}
    if (isTable) {
      formik.setValues({ ...initialValues, ...data, jsonData: jsonData ?? {} })
      Object.keys(jsonDataSchema).forEach((key) => {
        let {
          ui,
          schema = {
            type: 'object',
            properties: {},
          },
        } = getCurrent(key)
        if (ui.key === key) {
          uiList.push(ui)
          schemaList.push(schema)
        }
      })
    } else if (isUpdated) {
      let formikData = {}
      let favorite = objectPath.get(isUiChanged, `editFavorite`)
      let jsonData = objectPath.get(favorite, `jsonData`)
      let favoriteJson = favoriteDataKey
        ? objectPath.get(jsonData, favoriteDataKey)
        : jsonData
      let data = {}
      Object.keys(jsonDataSchema).forEach((key) => {
        let { ui, schema } = getCurrent(key)
        if (ui.key === key) {
          uiList.push(ui)
          schemaList.push(schema)
        }
        let name = objectPath.get(jsonDataSchema, key)
        let value =
          objectPath.get(favoriteJson, name) || objectPath.get(jsonData, name)
        objectPath.set(data, name, value)
      })
      formikData = {
        ...favorite,
        jsonData: data,
      }
      formik.setValues(formikData)
    } else {
      let data = {}
      Object.keys(jsonDataSchema).forEach((key) => {
        let { ui, schema } = getCurrent(key)
        if (ui.key === key) {
          uiList.push(ui)
          schemaList.push(schema)
        }
        let name = objectPath.get(jsonDataSchema, key)
        let value
        if (formDataObj.has(name)) {
          value = formDataObj.get(name)
        } else if (formDataObj.has(key)) {
          value = formDataObj.get(key)
        }
        objectPath.set(data, name, value)
      })
      updateValue('jsonData', data)
    }
    setUiList(uiList)
    setSchemaList(schemaList)
  }, [isOpen])

  const renderers = useMemo(
    () => createFavoriteRenderer(uiList),
    [JSON.stringify(uiList)],
  )

  return (
    <>
      <Modal show={isOpen} size={'xl'}>
        <Modal.Header className="p-3" closeButton onHide={closeModal}>
          <h5 className="mb-0">{mode}</h5>
        </Modal.Header>
        <Modal.Body className="p-0">
          <MuiThemeProvider theme={themeDefault}>
            <Row className="py-3 w-100 mx-auto">
              <Col sm="6">
                <Form.Label>
                  ชื่อสูตร
                  <span className="text-danger ml-1">
                    * {objectPath.get(formik, 'errors.name') || ''}
                  </span>
                </Form.Label>
              </Col>
              <Col sm="6">
                <Form.Label>
                  Template Type Code
                  <span className="text-danger ml-1">
                    *
                    {objectPath.get(
                      formik,
                      'errors.favoriteTemplateTypeCode',
                    ) || ''}
                  </span>
                </Form.Label>
              </Col>
              <Col sm="6">
                <Form.Control
                  autoComplete="off"
                  name="name"
                  onChange={(e) => updateValue('name', e.target.value)}
                  value={favorite.name}
                />
              </Col>
              <Col sm="6">
                <Form.Control
                  readOnly
                  name="favoriteTemplateTypeCode"
                  value={favorite.favoriteTemplateTypeCode}
                />
              </Col>
              <Col sm="12" className="mt-2">
                <Form.Label>รายละเอียด</Form.Label>
              </Col>
              <Col sm="12">
                <Form.Control
                  autoComplete="off"
                  as="textarea"
                  name="description"
                  onChange={(e) => updateValue('description', e.target.value)}
                  value={favorite.description}
                />
              </Col>
              <Col sm="12" className="mt-2">
                <Form.Label>ประเภท</Form.Label>
              </Col>
              <Col sm="12">
                <ButtonToolbar>
                  <ToggleButtonGroup
                    type="radio"
                    name="isPublic"
                    defaultValue={1}
                    value={favorite.isPublic}
                    onChange={(value) => updateValue('isPublic', value)}
                  >
                    <ToggleButton variant="outline-secondary" value={1}>
                      Public
                    </ToggleButton>
                    <ToggleButton variant="outline-secondary" value={0}>
                      Private
                    </ToggleButton>
                  </ToggleButtonGroup>
                </ButtonToolbar>
              </Col>
              <Col sm="12" className="mt-2">
                <hr />
                <Form.Label>รายการสูตร</Form.Label>
              </Col>
              <Col sm="12">
                <Row className="w-100 mx-auto">
                  {uiList.map((ui, index) => {
                    return (
                      <JsonForms
                        key={index}
                        data={jsonData}
                        schema={schemaList[index]}
                        uischema={ui}
                        config={{
                          jsonData,
                          isFavoriteModal: true,
                          favoriteDataKey,
                          updateValue,
                          mapFieldFavorite,
                        }}
                        renderers={renderers}
                        cells={materialCells}
                        onChange={(e) => {
                          let isTable = ['table', 'customTable'].includes(
                            uischema.tagname,
                          )
                          if (isTable) return
                          let { data } = e
                          let value = data
                          if (
                            favoriteDataKey &&
                            objectPath.has(data, favoriteDataKey)
                          ) {
                            value = objectPath.get(data, favoriteDataKey)
                          }
                          if (
                            Array.isArray(value) &&
                            value.length === 0 &&
                            !isTable
                          )
                            return
                          if (isOpen) {
                            updateValue(`jsonData`, value)
                          }
                        }}
                      />
                    )
                  })}
                </Row>
              </Col>
            </Row>
          </MuiThemeProvider>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={(e) => {
              formik.setFieldValue('isSaveAndUsed', true)
              formik.handleSubmit(e)
            }}
          >
            <FeatherIcon icon="check-circle" size="14" className="mr-1" />
            {mode}และใช้สูตร
          </Button>
          <Button
            variant="outline-success"
            onClick={(e) => {
              formik.setFieldValue('isSaveAndUsed', false)
              formik.handleSubmit(e)
            }}
          >
            <FeatherIcon icon="check-circle" size="14" className="mr-1" />
            {mode}
          </Button>
          <Button variant="outline-secondary" onClick={closeModal}>
            <FeatherIcon icon="x" size="14" className="mr-1" />
            ยกเลิก
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ModalCreate
